import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEmptySet } from '@fortawesome/pro-regular-svg-icons'
import { Empty as AntdEmpty } from 'antd'
import { EmptyProps } from 'antd/es/empty'
import cx from 'classnames'
import React from 'react'
import { colors } from '../style/constants'
import './styles.scss'
import { Typography } from '../AppTypography'

interface AppEmptyProps extends EmptyProps {
  // Horizontal arrangement. No value -> center
  justify?: 'start'
  // Vertical alignment. No value -> middle
  align?: 'top' | 'bottom'
  backgroundColor?: string
  title?: React.ReactNode
  actions?: React.ReactNode
}

const EmptySetImage = <FontAwesomeIcon icon={faEmptySet} size="3x" color={colors.neutral400} />
const EmptyFolder = (
  <svg xmlns="http://www.w3.org/2000/svg" width="39px" height="32px" viewBox="0 0 511 420" version="1.1">
    <g id="surface1">
      <path
        style={{
          fillRule: 'nonzero',
          fill: 'rgb(67.45098%,72.54902%,78.039216%)',
          fillOpacity: 1,
          strokeLinecap: 'butt',
          strokeLinejoin: 'miter',
          stroke: 'rgb(39.215686%,45.490196%,51.764706%)',
          strokeMiterlimit: 4
        }}
        d="M 19.862226 7.299107 L 28.80877 7.299107 C 30.246048 7.299107 31.508623 8.63006 31.508623 10.399405 L 31.508623 28.399405 C 31.508623 30.168452 30.246048 31.499405 28.80877 31.499405 L 3.208461 31.499405 C 1.771182 31.499405 0.508608 30.168452 0.508608 28.399405 L 0.508608 6.799107 C 0.508608 5.03006 1.771182 3.699107 3.208461 3.699107 L 15.351241 3.699107 C 15.619259 3.700893 15.889364 3.820238 16.099843 4.054167 C 16.099843 4.054464 16.100141 4.054762 16.100439 4.05506 L 18.370987 6.609524 L 18.372179 6.611012 C 18.75766 7.041071 19.29131 7.296429 19.862226 7.299107 Z M 19.862226 7.299107 "
        transform="matrix(13.102564,0,0,13.125,0,0)"
      />
      <path
        style={{ stroke: 'none', fill: 'rgb(39.215686%,45.490196%,51.764706%)' }}
        d="M 0.113281 380.992188 C 0.113281 402.53125 18.882812 419.992188 42.042969 419.992188 L 377.472656 419.992188 C 400.628906 419.992188 419.402344 402.53125 419.402344 380.992188 L 461.324219 185.992188 C 461.324219 164.449219 442.550781 146.988281 419.394531 146.988281 L 83.960938 146.988281 C 63.003906 146.988281 42.035156 166.488281 42.035156 185.996094 C 42.011719 192.304688 0.113281 374.304688 0.113281 380.992188 Z M 0.113281 380.992188 "
      />
      <path
        style={{ stroke: 'none', fill: 'rgb(94.117647%,95.294118%,96.078431%)' }}
        d="M 424.355469 167.996094 C 432.632812 167.996094 438.835938 175.59375 437.195312 183.722656 L 395.914062 388.472656 C 394.679688 394.59375 389.304688 399 383.070312 399 L 37.097656 399 C 28.820312 399 22.613281 391.402344 24.253906 383.273438 L 65.539062 178.523438 C 66.773438 172.402344 72.144531 167.996094 78.382812 167.996094 Z M 424.355469 167.996094 "
      />
      <path
        style={{ stroke: 'none', fill: 'rgb(39.215686%,45.490196%,51.764706%)' }}
        d="M 473.824219 37.109375 C 477.917969 41.210938 477.917969 47.859375 473.824219 51.960938 L 459 66.808594 C 454.90625 70.910156 448.269531 70.910156 444.175781 66.808594 C 440.082031 62.707031 440.082031 56.058594 444.175781 51.960938 L 459 37.109375 C 463.09375 33.007812 469.730469 33.007812 473.824219 37.109375 Z M 473.824219 37.109375 "
      />
      <path
        style={{ stroke: 'none', fill: 'rgb(39.215686%,45.490196%,51.764706%)' }}
        d="M 490.570312 90.921875 C 493.941406 89.796875 497.652344 90.675781 500.164062 93.191406 C 505.09375 98.128906 503.113281 106.535156 496.5 108.742188 L 471.917969 116.949219 C 468.546875 118.074219 464.835938 117.199219 462.324219 114.683594 C 457.394531 109.746094 459.375 101.339844 465.988281 99.132812 Z M 490.570312 90.921875 "
      />
      <path
        style={{ stroke: 'none', fill: 'rgb(39.215686%,45.490196%,51.764706%)' }}
        d="M 402.324219 14.414062 C 404.53125 7.792969 412.921875 5.808594 417.851562 10.746094 C 420.359375 13.261719 421.238281 16.980469 420.117188 20.355469 L 411.917969 44.980469 C 409.714844 51.605469 401.324219 53.589844 396.398438 48.652344 C 393.886719 46.136719 393.007812 42.417969 394.132812 39.042969 Z M 402.324219 14.414062 "
      />
    </g>
  </svg>
)

/**
 * This class is a wrap around [antd Empty](https://ant.design/components/empty/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * It is used to insure we are consistent in our use of different empty states.
 * This wrapper component will assure we can use this Empty state in all cases with different
 * image and text alignments and any other extra styling
 */
export function Empty({
  className,
  image = Empty.PRESENTED_IMAGE_FOLDER,
  justify,
  align,
  backgroundColor,
  style,
  title,
  description,
  actions,
  ...props
}: AppEmptyProps) {
  React.useEffect(() => {
    if (actions && !description) {
      // eslint-disable-next-line no-console -- Only a warning for devs
      console.warn("Empty component props 'actions' should be used with 'description'")
    }
    if (title && !description) {
      // eslint-disable-next-line no-console -- Only a warning for devs
      console.warn("Empty component prop 'title' should be used with 'description'")
    }
  }, [actions, title, description])
  return (
    <AntdEmpty
      {...props}
      // title and actions cannot be used without description
      {...(description
        ? {
            description: (
              <>
                {title ? <Typography.Text className="app-empty-title">{title}</Typography.Text> : null}
                {description ? <Typography.Paragraph>{description}</Typography.Paragraph> : null}
                {/* eslint-disable-next-line react/jsx-no-useless-fragment -- I think it was done like this for a reason */}
                {actions ? <>{actions}</> : null}
              </>
            )
          }
        : {})}
      style={{ backgroundColor, ...(backgroundColor && { borderRadius: '12px', color: colors.neutral500 }), ...style }}
      image={image}
      className={cx(
        'app-empty',
        backgroundColor && 'u-padding--medium-2',
        image === EmptySetImage && 'ant-empty-image--custom',
        justify && `app-empty-justify--${justify}`,
        align && `app-empty-align--${align}`,
        className
      )}
    />
  )
}

Empty.PRESENTED_IMAGE_FOLDER = EmptyFolder
Empty.PRESENTED_IMAGE_SIMPLE = AntdEmpty.PRESENTED_IMAGE_SIMPLE
Empty.PRESENTED_IMAGE_EMPTY_SET = EmptySetImage
export default Empty
